// AdminWeeklySchedule.jsx - Haftalık açılış/kapanış saatleri yönetimi
import React, { useState, useEffect } from 'react';
import Card from '../common/Card';
import Button from '../common/Button';
import { getWeeklySchedule, setWeeklySchedule } from '../../utils/api';

const AdminWeeklySchedule = () => {
  const [schedule, setSchedule] = useState({
    monday: { isOpen: true, openTime: '09:00', closeTime: '22:00', slotDuration: 60 },
    tuesday: { isOpen: true, openTime: '09:00', closeTime: '22:00', slotDuration: 60 },
    wednesday: { isOpen: true, openTime: '09:00', closeTime: '22:00', slotDuration: 60 },
    thursday: { isOpen: true, openTime: '09:00', closeTime: '22:00', slotDuration: 60 },
    friday: { isOpen: true, openTime: '09:00', closeTime: '22:00', slotDuration: 60 },
    saturday: { isOpen: false, openTime: '09:00', closeTime: '18:00', slotDuration: 60 },
    sunday: { isOpen: false, openTime: '09:00', closeTime: '18:00', slotDuration: 60 }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const dayNames = {
    monday: 'Pazartesi',
    tuesday: 'Salı',
    wednesday: 'Çarşamba',
    thursday: 'Perşembe',
    friday: 'Cuma',
    saturday: 'Cumartesi',
    sunday: 'Pazar'
  };

  useEffect(() => {
    loadSchedule();
  }, []);

  const loadSchedule = async () => {
    try {
      setLoading(true);
      const data = await getWeeklySchedule();
      if (data && Object.keys(data).length > 0) {
        setSchedule(data);
      }
    } catch (error) {
      console.error('Haftalık program yüklenemedi:', error);
      setMessage({ type: 'error', text: 'Program yüklenirken hata oluştu: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDayChange = (day, field, value) => {
    setSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: field === 'isOpen' ? value : value
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      await setWeeklySchedule(schedule);
      setMessage({ type: 'success', text: 'Haftalık program başarıyla kaydedildi!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Program kaydedilemedi:', error);
      setMessage({ type: 'error', text: 'Kaydetme hatası: ' + error.message });
    } finally {
      setSaving(false);
    }
  };

  const copyToAll = (sourceDay) => {
    const sourceDaySchedule = schedule[sourceDay];
    const newSchedule = {};
    Object.keys(schedule).forEach(day => {
      newSchedule[day] = { ...sourceDaySchedule };
    });
    setSchedule(newSchedule);
    setMessage({ type: 'info', text: `${dayNames[sourceDay]} ayarları tüm günlere kopyalandı` });
    setTimeout(() => setMessage({ type: '', text: '' }), 2000);
  };

  if (loading) {
    return (
      <Card>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Haftalık program yükleniyor...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Haftalık Çalışma Saatleri</h2>
        <p className="text-gray-400">Her gün için açılış/kapanış saatlerini ve rezervasyon slot süresini ayarlayın</p>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-900 border border-green-600 text-green-200' :
          message.type === 'error' ? 'bg-red-900 border border-red-600 text-red-200' :
          'bg-blue-900 border border-blue-600 text-blue-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Schedule Table */}
      <div className="overflow-x-auto mb-6">
        <table className="min-w-full divide-y divide-gray-700">
          <thead className="bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Gün
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
                Açık
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Açılış Saati
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Kapanış Saati
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Slot Süresi (dk)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                İşlem
              </th>
            </tr>
          </thead>
          <tbody className="bg-gray-800 divide-y divide-gray-700">
            {Object.keys(schedule).map((day) => (
              <tr key={day} className={!schedule[day].isOpen ? 'opacity-50' : ''}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-medium text-gray-100">{dayNames[day]}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <input
                    type="checkbox"
                    checked={schedule[day].isOpen}
                    onChange={(e) => handleDayChange(day, 'isOpen', e.target.checked)}
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-600 rounded bg-gray-700"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="time"
                    value={schedule[day].openTime}
                    onChange={(e) => handleDayChange(day, 'openTime', e.target.value)}
                    disabled={!schedule[day].isOpen}
                    className="bg-gray-700 border border-gray-600 text-gray-100 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="time"
                    value={schedule[day].closeTime}
                    onChange={(e) => handleDayChange(day, 'closeTime', e.target.value)}
                    disabled={!schedule[day].isOpen}
                    className="bg-gray-700 border border-gray-600 text-gray-100 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={schedule[day].slotDuration}
                    onChange={(e) => handleDayChange(day, 'slotDuration', parseInt(e.target.value))}
                    disabled={!schedule[day].isOpen}
                    className="bg-gray-700 border border-gray-600 text-gray-100 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value={30}>30 dakika</option>
                    <option value={60}>60 dakika</option>
                    <option value={90}>90 dakika</option>
                    <option value={120}>120 dakika</option>
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => copyToAll(day)}
                    className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                  >
                    Tümüne Kopyala
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button
          onClick={loadSchedule}
          variant="secondary"
          disabled={saving}
        >
          Geri Al
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-green-600 hover:bg-green-700"
        >
          {saving ? (
            <>
              <svg className="animate-spin h-5 w-5 mr-2 inline" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Kaydediliyor...
            </>
          ) : (
            'Kaydet'
          )}
        </Button>
      </div>

      {/* Info */}
      <div className="mt-6 p-4 bg-blue-900 bg-opacity-30 border border-blue-700 rounded-lg">
        <div className="flex">
          <svg className="w-5 h-5 text-blue-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-200">
            <p className="font-semibold mb-1">Bilgilendirme:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Açılış ve kapanış saatleri arasında rezervasyon alınabilir</li>
              <li>Slot süresi, minimum rezervasyon süresini belirler</li>
              <li>"Tümüne Kopyala" butonu ile bir günün ayarlarını tüm günlere uygulayabilirsiniz</li>
              <li>Değişiklikler kaydet butonuna basılmadan geçerli olmaz</li>
            </ul>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default AdminWeeklySchedule;
